import React, { useRef } from 'react';
import { ApolloError } from '@apollo/client';
import { t } from '@lingui/macro';
import { Box, useBreakpoint } from '@landr/maestro';
import Carousel from 'components/Carousels/components/Carousel';
import Title from 'components/Carousels/components/Title';
import Byline from 'components/Carousels/components/Byline';
import { CarouselProvider } from 'components/Carousels/contexts/CarouselContext';
import { GenericCarousel } from 'components/GenericCarousel/GenericCarousel';
import { CarouselConfigType } from 'components/GenericCarousel/GenericCarousel.type';
import { PackCardProps } from 'components/Cards/PackCard/PackCard';
import { Slice } from 'components/Slice';
import { CommonCard } from 'types';
import { Pack, Repack } from 'types/generated/graphql';
import useDetectTargetIsInsideViewport from 'hooks/useDetectTargetIsInsideViewport/useDetectTargetIsInsideViewport';
import {
    CollectionDetailsCarouselWrapper,
    CollectionDetailsHeader,
} from './PackDetailsCarousel.styled';
import { usePackDetailsCarousel } from './usePackDetailsCarousel';

type PackDetailsCarouselInnerProps = {
    data?: Pack[] | Repack[];
    item: Pack | Repack;
    loading: boolean;
    error?: ApolloError;
    isMobile: boolean;
    cardConfig: CarouselConfigType<Pack>;
    cardComponent: React.FC<PackCardProps>;
};

const _PackDetailsCarousel: React.FC<PackDetailsCarouselInnerProps> = ({
    item,
    data,
    loading,
    error,
    isMobile,
    cardConfig,
    cardComponent,
}) => {
    const title = t`Packs you may also like`;
    const byline = t`Similar to ${item.name}`;

    if (error || (!loading && !data) || data?.length === 0) {
        return null;
    }

    return (
        <CarouselProvider
            carouselGuid={item.guid}
            carouselName={item.name}
            withPreview={false}
        >
            <Slice>
                <CollectionDetailsCarouselWrapper>
                    {!isMobile && (
                        <>
                            <CollectionDetailsHeader>
                                {title}
                            </CollectionDetailsHeader>
                            <GenericCarousel
                                title={byline}
                                items={data || []}
                                isFetching={loading}
                                gutter="md"
                                arrowOffset="2xl"
                                {...cardConfig}
                                cardComponent={
                                    cardComponent as CommonCard<unknown>
                                }
                            />
                        </>
                    )}
                    {isMobile && (
                        <Box
                            data-component="Carousels"
                            aria-live="polite"
                            aria-busy={loading}
                        >
                            <Box mb="xl">
                                <Title content={title} isLoading={loading} />
                                <Byline content={byline} isLoading={loading} />
                            </Box>
                            <Carousel items={data} isLoading={loading} />
                        </Box>
                    )}
                </CollectionDetailsCarouselWrapper>
            </Slice>
        </CarouselProvider>
    );
};

export type PackDetailsCarouselProps = {
    item: Pack | Repack;
};

const PackDetailsCarousel: React.FC<PackDetailsCarouselProps> = ({ item }) => {
    const ref = useRef<HTMLDivElement>(null);
    const isMobile = useBreakpoint('md');
    const isInsideViewport = useDetectTargetIsInsideViewport({ ref });
    const { data, loading, error, cardConfig, cardComponent } =
        usePackDetailsCarousel({ item, skip: !isInsideViewport });

    return (
        <Box ref={ref}>
            <_PackDetailsCarousel
                data={data}
                item={item}
                loading={loading || !isInsideViewport}
                error={error}
                isMobile={isMobile}
                cardConfig={cardConfig}
                cardComponent={cardComponent}
            />
        </Box>
    );
};

export default PackDetailsCarousel;
